/**
 * Task #3846 — Download e condivisione artefatti PA.
 *
 * Contratto condiviso BE↔FE per l'export tipizzato degli artefatti del
 * workspace PA e per la condivisione via Messenger (snapshot file durevole,
 * MAI URL firmati a scadenza, MAI accesso alla conversazione PA d'origine).
 */
import { z } from 'zod';
import type { ArtifactKind } from './pa-workspace';

/** Formati di export supportati (unione su tutti i kind). */
export const ARTIFACT_EXPORT_FORMAT_VALUES = [
  'md',
  'pdf',
  'csv',
  'xlsx',
  'png',
  'svg',
  'html',
  'json',
  'mmd',
  'original',
] as const;

export const ArtifactExportFormatSchema = z.enum(ARTIFACT_EXPORT_FORMAT_VALUES);
export type ArtifactExportFormat = z.infer<typeof ArtifactExportFormatSchema>;

/**
 * Mappa kind → formati disponibili (il PRIMO è il default del bottone
 * Scarica). Guida sia il dispatcher BE sia il menu FE: un formato fuori
 * lista per il kind viene rifiutato dal server con 400.
 */
export const ARTIFACT_EXPORT_FORMATS: Record<ArtifactKind, readonly ArtifactExportFormat[]> = {
  markdown: ['md', 'pdf'],
  code: ['original'],
  table: ['csv', 'xlsx'],
  chart: ['png', 'svg'],
  diagram: ['png', 'mmd'],
  html: ['html', 'pdf'],
  react: ['original'],
  image: ['original'],
  video: ['original'],
  audio: ['original'],
  file: ['original'],
  form: ['json'],
  map: ['json'],
  timeline: ['json'],
  kanban: ['json'],
  mindmap: ['json'],
  action: ['json'],
};

/** Etichette leggibili per il menu di scelta formato. */
export const ARTIFACT_EXPORT_FORMAT_LABELS: Record<ArtifactExportFormat, string> = {
  md: 'Markdown (.md)',
  pdf: 'PDF',
  csv: 'CSV',
  xlsx: 'Excel (.xlsx)',
  png: 'Immagine PNG',
  svg: 'Immagine SVG',
  html: 'Pagina HTML',
  json: 'JSON',
  mmd: 'Mermaid (.mmd)',
  original: 'File originale',
};

/** Target di condivisione: collega (DM, creata se non esiste) o conversazione esistente. */
export const ShareArtifactTargetSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('user'), userId: z.string().uuid() }),
  z.object({ kind: z.literal('conversation'), conversationId: z.string().uuid() }),
]);
export type ShareArtifactTarget = z.infer<typeof ShareArtifactTargetSchema>;

export const ShareArtifactRequestSchema = z.object({
  format: ArtifactExportFormatSchema,
  target: ShareArtifactTargetSchema,
  /** Messaggio opzionale che accompagna l'allegato. */
  note: z.string().max(2000).optional(),
});
export type ShareArtifactRequest = z.infer<typeof ShareArtifactRequestSchema>;

export const SharedArtifactAttachmentSchema = z.object({
  name: z.string(),
  size: z.number(),
  type: z.string(),
  /** Chiave storage durevole (stesso formato degli allegati Messenger), MAI URL firmato. */
  url: z.string(),
});
export type SharedArtifactAttachment = z.infer<typeof SharedArtifactAttachmentSchema>;

export const ShareArtifactResponseSchema = z.object({
  conversationId: z.string(),
  messageId: z.string(),
  attachment: SharedArtifactAttachmentSchema,
});
export type ShareArtifactResponse = z.infer<typeof ShareArtifactResponseSchema>;
